import { AnimatePresence, m } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../cn";
import { useMotionInitial, useMotionTransition } from "../motion";
import { LicensePlate } from "./LicensePlate";

/** How long the "Gekopieerd" confirmation stays visible before reverting. */
const COPY_CONFIRMATION_MS = 1600;

interface CopyPlateProps {
  readonly value: string;
  readonly size?: "sm" | "md" | "lg";
  readonly className?: string;
}

/**
 * A {@link LicensePlate} that doubles as a click-to-copy control: clicking it
 * copies the plate identifier to the clipboard and swaps in a brief
 * "Gekopieerd" confirmation (icon + screen-reader announcement), so callers
 * don't need a separate copy button next to the plate.
 */
export function CopyPlate({ value, size = "md", className }: CopyPlateProps) {
  const [copied, setCopied] = useState(false);
  const swapTransition = useMotionTransition();
  const copiedInitial = useMotionInitial({ opacity: 0, x: -6 });
  const idleInitial = useMotionInitial({ opacity: 0 });
  const timeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  function handleCopy(): void {
    // navigator.clipboard is undefined outside secure (HTTPS) contexts and in
    // some older/embedded browsers, so accessing writeText on it throws
    // synchronously rather than rejecting - guard its presence before
    // calling it. A denied-permission rejection from a supported browser is
    // still just a rejected promise, so that path stays caught below; the
    // copy is best-effort either way, hence no error is surfaced to the user.
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(value).catch(() => {});
    }

    setCopied(true);
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      setCopied(false);
    }, COPY_CONFIRMATION_MS);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Kenteken kopiëren"
      aria-label={`Kopieer kenteken ${value}`}
      className={cn("group inline-flex items-center gap-1.5", className)}
    >
      <LicensePlate value={value} size={size} />
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <m.span
            key="copied"
            initial={copiedInitial}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={swapTransition}
            className="inline-flex items-center gap-1 text-xs font-medium text-success"
          >
            <Check className="size-3.5" aria-hidden="true" />
            Gekopieerd
          </m.span>
        ) : (
          <m.span
            key="idle"
            initial={idleInitial}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={swapTransition}
            className="text-muted-foreground transition-colors group-hover:text-foreground"
          >
            <Copy className="size-3.5" aria-hidden="true" />
          </m.span>
        )}
      </AnimatePresence>
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? `Kenteken ${value} gekopieerd` : ""}
      </span>
    </button>
  );
}
